import { useEffect, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { toast } from "react-toastify";
import { useQuery } from "@tanstack/react-query";
import CheckIcon from "../components/Icons/CheckIcon";
import QuizXIcon from "../components/Icons/QuizXIcon";
import Spinner from "../components/Shared/Spinner";
import { useUserDataStore } from "../stores/user";
import { axios } from "../lib/axios";
import { StartCBQSession } from "../api/Quiz/StartCBQSession";
import { GetCBQExamSessions } from "../api/Quiz/GetCBQExamSessions";
import { withNativeParam } from "../lib/nativeMode";

// Confirm-details page for a standalone CBQ exam, addressed by *exam* id.
// Lets external clients (e.g. the mobile webview) deep-link an exam without
// pre-creating a session: /cbq-exam/start/:examId?token=...
// The session is only created when the student clicks Confirm, then we
// navigate to the session-scoped route /cbq-exam/:sessionId.
const CbqStart = () => {
  const { examId } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  // No student endpoint returns a CBQ exam's title by exam id, so callers
  // (Lecture, mobile webview) pass it in the URL; fall back to a generic label.
  const title = searchParams.get("title") || "CBQ Exam";
  const navigate = useNavigate();
  const { userData, setUserData } = useUserDataStore();
  const [isFetching, setIsFetching] = useState(false);
  const [starting, setStarting] = useState(false);

  const getUserData = async (token: string) => {
    setIsFetching(true);
    const { data } = await axios.get(`/student/profile`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (data) setUserData(data);
    setIsFetching(false);
  };

  useEffect(() => {
    if (token) {
      getUserData(token);
    }
  }, [token]);

  // Previous attempts for this exam — submitted ones open their result,
  // an in-progress one can be resumed.
  const { data: sessionsDTO, isLoading: isSessionsLoading } = useQuery({
    queryFn: () => GetCBQExamSessions(examId || ""),
    queryKey: ["cbq-exam-sessions", examId],
    enabled: !!examId,
    staleTime: 0,
  });
  const pastSessions = sessionsDTO?.data ?? [];

  // Where the exam/result pages should send the student back to. Forward the
  // caller's origin (e.g. the lecture) when given; fall back to this page.
  const backTo =
    (location.state as { from?: string } | null)?.from ??
    window.location.pathname + window.location.search;

  // Start (or resume, idempotent) an attempt and open the exam page.
  const handleConfirmClick = async () => {
    if (!examId || starting) return;
    setStarting(true);
    const res = await StartCBQSession(examId);
    setStarting(false);
    if (res.error || !res.data?.id) {
      toast.error(res.error?.message || "Could not start CBQ exam");
      return;
    }
    navigate(withNativeParam(`/cbq-exam/${res.data.id}`), {
      state: { from: backTo },
    });
  };

  if (isFetching)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner />
      </div>
    );

  return (
    <div className=" min-h-screen flex flex-col justify-center items-center bg-[#dfe7eb]">
      <div className="px-4 sm:w-fit w-full mx-auto">
        <div className="sm:w-[486px]  w-full shadow-quizShadow bg-white rounded overflow-hidden pb-16">
          <div className="h-11 bg-[#4d4c4d] flex justify-between items-center px-2.5 text-white">
            <p className="font-semibold">Confirm Details</p>
          </div>
          <div className="text-[#333333] mt-12 sm:mx-0 mx-5">
            <div className="bg-[#f8f9fa] border rounded border-[#B3B3B3] sm:w-[372px] mx-auto py-[18px] sm:px-6 px-3">
              <div className="flex sm:gap-8 gap-4">
                <div>
                  <p>First Name:</p>
                  <p>Last Name:</p>
                  <p>Exam Name:</p>
                </div>
                <div>
                  <p> {userData?.firstName}</p>
                  <p> {userData?.lastName}</p>
                  <p>{title}</p>
                </div>
              </div>
            </div>
            <p className="text-center py-8">Are the details above correct?</p>
            <div className="flex justify-center gap-4">
              <button
                className="bg-[#8cc63f] text-white flex items-center justify-center h-10 w-32 rounded gap-3 disabled:opacity-60"
                onClick={handleConfirmClick}
                disabled={starting}
              >
                <CheckIcon /> {starting ? "Starting..." : "Confirm"}
              </button>
              <button
                onClick={() => navigate(-1)}
                className="bg-[#8cc63f] text-white flex items-center justify-center h-10 w-24 rounded gap-2"
              >
                <QuizXIcon /> Cancel
              </button>
            </div>
            {isSessionsLoading ? (
              <div className="flex justify-center mt-6">
                <Spinner />
              </div>
            ) : (
              pastSessions.length > 0 && (
                <div className="sm:mx-6 mx-5 mt-6">
                  <p className="font-semibold text-center mb-2">
                    Previous Attempts:
                  </p>
                  <div className="max-h-[200px] overflow-y-auto border rounded border-[#B3B3B3]">
                    {pastSessions.map((session, i) => {
                      const isSubmitted = session.status === "submitted";
                      const isInProgress = session.status === "in_progress";
                      const clickable = isSubmitted || isInProgress;
                      const openAttempt = () =>
                        navigate(
                          withNativeParam(
                            isSubmitted
                              ? `/cbq-exam/${session.id}/result`
                              : `/cbq-exam/${session.id}`
                          ),
                          { state: { from: backTo } }
                        );
                      return (
                        <div
                          key={session.id}
                          onClick={clickable ? openAttempt : undefined}
                          role={clickable ? "button" : undefined}
                          className={`px-3 py-2 border-b last:border-b-0 border-black/10 transition-colors ${
                            clickable ? "cursor-pointer hover:bg-black/5" : ""
                          }`}
                        >
                          <div className="flex justify-between items-center">
                            <p className="font-semibold">
                              Attempt #{session.attemptNumber ?? i + 1}
                            </p>
                            <p>
                              Score:{" "}
                              {session.totalScore != null
                                ? session.totalScore
                                : "—"}
                            </p>
                            <p
                              className={`font-semibold ${
                                isSubmitted
                                  ? "text-green-500"
                                  : "text-yellow-500"
                              }`}
                            >
                              {isInProgress ? "In progress" : session.status}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CbqStart;
